import { z } from 'zod';

import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { CostSchema } from './shared-schemas.js';
import {
  ZodAdultsCount,
  ZodChildrenCount,
  ZodDepartureDate,
  ZodDestinationAirport,
  ZodGoogleCabinClass,
  ZodInfantCount,
  ZodOriginAirport,
  ZodReturnDate,
} from './zod-types.js';

/**
 * Airline information schema
 */
export const AirlineInfoSchema = z.object({
  iata_code: z.string(),
  icao_code: z.string().optional(),
  name: z.string(),
  logo_url: z.string().optional(),
  alternativeName: z.string().optional(),
});

export type AirlineInfo = z.infer<typeof AirlineInfoSchema>;

/**
 * Airport information schema
 */
export const AirportInfoSchema = z.object({
  id: z.string().optional().describe('Airport id'),
  iata_code: z.string(),
  icao_code: z.string().optional(),
  airport_name: z.string(),
  city_name: z.string().optional(),
  country_name: z.string().optional(),
  country_code: z.string().optional().describe('ISO (2 letter)'),
});

export type AirportInfo = z.infer<typeof AirportInfoSchema>;

/**
 * Flight segment schema - represents a single flight leg
 * This schema supports both search results and itinerary items
 */
export const FlightSegmentSchema = z
  .object({
    airline: AirlineInfoSchema.describe('Airline'),
    flightNumber: z.string(),
    aircraftType: z.string().optional(),
    departure: z.string().describe('Departure time (ISO)'),
    origin: AirportInfoSchema.describe('Origin airport'),
    arrival: z.string().describe('Arrival time (ISO)'),
    destination: AirportInfoSchema.describe('Destination airport'),
    duration: z.number().optional().describe('Duration in minutes'),
    travelClass: z.string().optional().describe('Travel class (e.g. Economy, Business, First)'),
    overnight: z.boolean().optional().describe('Is overnight flight?'),
    includes: z.array(z.string()).optional().describe('Flight amenities'),
    operatedBy: z.string().optional().describe('Operated by (airline)'),
  })
  .describe('Flight segment schema - represents a single flight leg');

export type FlightSegment = z.infer<typeof FlightSegmentSchema>;

/**
 * Passengers schema
 */
export const PassengersSchema = z.object({
  adults: z.number().default(1).describe('Adults'),
  children: z.number().default(0).describe('Children'),
  infants: z.number().default(0).describe('Infants on lap'),
});

/**
 * Flight option schema - represents a complete flight option with pricing
 * Used for flight search results displayed in FlightSearchWidget
 *
 * Note: cost and passengers are optional with defaults to handle cases where
 * external APIs (like Google Flights via SerpAPI) don't provide all fields.
 */
/**
 * Search context stored with each FlightOption document.
 * Captures the original search parameters so the flight can be re-searched for price rechecking.
 * Mirrors SearchGoogleFlightsBaseSchema fields but defined separately to avoid circular ordering.
 */
export const FlightOptionSearchSchema = z.object({
  origin: z.array(z.string()),
  destination: z.array(z.string()),
  departureDate: z.string(),
  returnDate: z.string().nullish(),
  adults: z.number().optional(),
  children: z.number().optional(),
  infants: z.number().optional(),
  cabinClass: z.string().optional(),
  directFlightsOnly: z.boolean().optional(),
  searchType: z.enum(['one-way', 'round-trip']),
  currency: z.string().optional(),
});

export type FlightOptionSearch = z.infer<typeof FlightOptionSearchSchema>;

export const FlightOptionSchema = z.object({
  id: z.string().optional().describe('Unique id for this flight option'),
  cost: CostSchema.optional().describe('Total price and currency for the flight (may not be available)'),
  passengers: PassengersSchema.optional().default({ adults: 1, children: 0, infants: 0 }),
  outboundSegments: z.array(FlightSegmentSchema).optional().describe('Segments for outbound flight'),
  returnSegments: z.array(FlightSegmentSchema).optional().describe('Segments for return flight'),
  roundTrip: z.boolean().optional(),
  bookingToken: z.string().optional().describe('Used to get booking options for a flight.'),
  departureToken: z
    .string()
    .optional()
    .describe(
      'Used to select the flight and get returning flights (for round trip) or flights for the next leg of itinerary (for multi-city).',
    ),

  // Deprecated: use outboundSegments and returnSegments instead
  segments: z.array(FlightSegmentSchema).optional(),

  // Traceability when storing flight options in Firestore (optional for in-memory payloads)
  source: z.string().optional().describe("Data source, like 'serpapi'"),
  fetchedAt: zFirestampOptional(),
  searchId: z.string().optional().describe('Id of the search execution; all options from the same fetch share this'),
  search: FlightOptionSearchSchema.optional().describe('Original search parameters for re-searching'),

  // Availability & price tracking (populated by RecheckFlightPrice flow)
  available: z.boolean().optional().describe('Whether this flight is still bookable after a recheck'),
  priceHistory: z
    .array(
      z.object({
        cost: CostSchema,
        fetchedAt: zFirestampOptional(),
        trigger: z.enum(['manual', 'auto']).optional().default('manual'),
      }),
    )
    .optional()
    .describe('Historical prices from rechecks, newest first. Last entry is original search price.'),
});

export type FlightOption = z.infer<typeof FlightOptionSchema>;
export type FlightOptionIn = z.input<typeof FlightOptionSchema>;

export const FlightSearchTypeSchema = z.enum(['one-way', 'round-trip']);
export type FlightSearchType = z.infer<typeof FlightSearchTypeSchema>;

/**
 * Base schema for flight searches (tool/workflow input).
 * Does not include SerpApi-specific token fields.
 */
export const SearchGoogleFlightsBaseSchema = z.object({
  origin: z.array(ZodOriginAirport),
  destination: z.array(ZodDestinationAirport),
  departureDate: ZodDepartureDate,
  returnDate: ZodReturnDate.nullish(),
  adults: ZodAdultsCount.optional(),
  children: ZodChildrenCount.optional(),
  infants: ZodInfantCount.optional(),
  cabinClass: ZodGoogleCabinClass.default('Economy').optional(),
  directFlightsOnly: z.boolean().optional(),
  searchType: FlightSearchTypeSchema,
});

/**
 * The Zod schema for the input of the `searchFlights` tool and related workflows.
 * This is the public-facing schema used by tools and widgets.
 */
export const SearchGoogleFlightsSchema = SearchGoogleFlightsBaseSchema;

/**
 * Internal schema used by SerpApi integration.
 * Extends the base search schema with SerpApi token fields.
 */
export const SearchGoogleFlightsInternalSchema = SearchGoogleFlightsBaseSchema.extend({
  departureToken: z.string().optional(),
  bookingToken: z.string().optional(),
});

export type SearchGoogleFlightsInput = z.infer<typeof SearchGoogleFlightsSchema>;
export type SearchGoogleFlightsInternalInput = z.infer<typeof SearchGoogleFlightsInternalSchema>;

export const GoogleFlightsSearchResultsSchema = z.object({
  search: SearchGoogleFlightsSchema,
  results: z.array(FlightOptionSchema),
  total: z.number(),
  source: z.string(),
  searchId: z.string().optional(),
  message: z.string().optional().describe('Guidance for presenting these results to the user.'),
});

export const BookingLegSchema = z.looseObject({
  direction: z.enum(['together', 'departing', 'returning']).describe('Which part of the trip this leg covers'),
  providerName: z.string().describe('Airline or booking provider name'),
  providerLogo: z.string().optional().describe('Provider logo URL'),
  airline: z.boolean().nullish().describe('Whether the provider is the operating airline (vs a third-party booking site)'),
  flightNumbers: z.array(z.string()).optional().describe('Marketed flight numbers (e.g. ["SK 1678", "SK 2535"])'),
  cost: CostSchema.describe('Price for this leg'),
  localCosts: z.array(CostSchema).nullish().describe('Local currency prices for this leg'),
  baggagePrices: z.array(z.string()).nullish().describe('Baggage prices for this leg'),
  bookingRequest: z
    .looseObject({
      url: z.string().describe('URL of the booking request'),
      postData: z.string().describe('Post data for the booking request'),
    })
    .describe('Booking request for this leg'),
});

export type BookingLeg = z.infer<typeof BookingLegSchema>;

export const GoogleFlightBookingOptionSchema = z.object({
  title: z.string().nullish().describe('Title of the booking option (may be null from API)'),
  cost: CostSchema.describe('Total price and currency for the booking option'),
  localCosts: z.array(CostSchema).nullish().describe('Total local prices for the booking option'),
  providerName: z.string().describe('Combined provider name(s) for display'),
  legs: z.array(BookingLegSchema).describe('Booking legs: 1 = together option, 2 = separate tickets'),
});

export type GoogleFlightBookingOption = z.infer<typeof GoogleFlightBookingOptionSchema>;

export type GoogleFlightsSearchResults = z.infer<typeof GoogleFlightsSearchResultsSchema>;

export const GoogleFlightsBookingOptionsResultsSchema = z.object({
  search: SearchGoogleFlightsInternalSchema,
  results: z.array(GoogleFlightBookingOptionSchema),
  total: z.number(),
  source: z.string(),
});

export type GoogleFlightsBookingOptionsResults = z.infer<typeof GoogleFlightsBookingOptionsResultsSchema>;
